import styled, { css } from 'styled-components';
import { Grid } from '_utils/components/Corporate';
import { NavLink } from 'react-router-dom';
import StyledContentRichText from '_components/Corporate/ContentRichText/StyledContentRichText';
import { spacing } from '_utils/props';

import {
    mq,
    heading_xl,
    heading_l,
    heading_m,
    heading_s,
    heading_xs,
    text_l,
    text_s,
    text_xscap,
    svg,
    heading_xxl
} from '_utils/styles';

function background({ theme, $background }) {
  return css`
    background-color: ${theme.colors.background[$background]};
  `;
}

export const Container = styled.div<{ $background: string }>`
  ${background}
  width: 100%;
  position: relative;
  overflow: hidden;

  &.featured-image {
    ${mq('lg')} {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      min-height: 552px;
    }

    ${mq('xl')} {
      min-height: 640px;
    }

    /* ie11 cant do margin auto (Grid.Container) in flex */
    @media all and (-ms-high-contrast: none) {
      display: block;
    }
    @media print {
      display: flex;
      margin-top: 20px;
    }
  }

  &.bg-tertiary {
    color: ${({ theme }) => theme.colors.element.white};
  }
  @media print {
    &.bg-tertiary {
      color: ${({ theme }) => theme.colors.element.black};
    }
    background-color: ${({ theme }) => theme.colors.background.primary};
    page-break-inside: avoid;
  }
`;

// offset for secondary item container overlap
// (accounts for grid gutters at passed in size)
const secondaryContainerMarginOffset = (size) => ({ theme }) =>
  theme.grid.gutter.size[size] / 2 + 32;

export const Column = styled(Grid.Item)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  /* match container size, for circular image */
  .featured-image & {
    ${mq('lg')} {
      min-height: 552px;
    }

    ${mq('xl')} {
      min-height: 640px;
    }
  }
`;

export const Row = styled(Grid.Row)`
  .featured-image & {
    ${mq('lg')} {
      height: 100%;
    }
  }

  .secondary-items & {
    padding-bottom: 32px;
  }

  .secondary-items.featured-image & {
    ${mq('lg')} {
      padding-bottom: 0;
    }
  }
`;

const defaultDetailsTopPadding = ({ theme }) => css`
  /* 40px */
  ${spacing({ theme, $pt: 8 })}

  /* 64px */
  ${mq('md')} {
    ${spacing({ theme, $pt: 7 })}
  }

  /* 120px */
  ${mq('lg')} {
    ${spacing({ theme, $pt: 9 })}
  }
`;

const defaultDetailsBottomPadding = ({ theme }) => css`

  /* 40px */
  ${spacing({ theme, $pb: 8 })}

  /* 64px */
  ${mq('md')} {
    ${spacing({ theme, $pb: 7 })}
  }

  /* 120px */
  ${mq('lg')} {
    ${spacing({ theme, $pb: 9 })}
  }
`;

export const DetailsContainer = styled.div`
  width: '100%';
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  z-index: 3;

  .simple-image & {
    padding-top: 4px;
    ${defaultDetailsBottomPadding}

    ${mq('lg')} {
      ${({ theme }) => spacing({ theme, $py: 8 })}
    }
  }

  .featured-image & {
    ${defaultDetailsTopPadding}
    ${defaultDetailsBottomPadding}

    ${mq('lg')} {
      ${({ theme }) => spacing({ theme, $py: 8 })}
    }
  }

  .no-image & {
    ${defaultDetailsTopPadding}
    ${defaultDetailsBottomPadding}
  }
`;

export const SmallImageContainer = styled.div`
  /* ie11 needs height */
  @media all and (-ms-high-contrast: none) {
    height: 100%;
  }

  width: 100%;
  ${({ theme }) => spacing({ theme, $mt: 8, $mb: '4px' })}

  ${mq('lg')} {
    ${({ theme }) => spacing({ theme, $my: 8 })}
  }
`;

export const LargeImageContainer = styled.div<{ $background: string }>`
  ${background}
  display: block;
  width: 100%;
  height: 324px;
  position: relative;

  ${mq('md')} {
    height: 432px;
  }

  ${mq('lg')} {
    position: absolute;
    width: 56%;
    left: 0;
    top: 0;
    height: 100%;

    .right-image & {
      left: auto;
      right: 0;
    }
  }

  @media print {
    width: 50%;
  }
`;

const circleConfig = {
  left: css`
    left: 0;

    img {
      left: 0;
      transform: translateX(-11.5%);
    }
  `,
  right: css`
    right: 0;

    img {
      right: 0;
      transform: translateX(11.5%);
    }
  `
};

const handleCircleConfig = ({ theme, $config: { background, imageSize, imagePosition } }) => {
  if (imageSize !== 'featured') {
    return css`
      display: none;
    `;
  }

  return css`
    ${circleConfig[imagePosition]}

    img {
      background-color: ${theme.colors.background[background]};
    }
  `;
};

export const Circle = styled.div<{$config?: any}>`
  ${mq.lessThan('lg')} {
    display: none;
  }

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 171%;
  width: 2048px;

  img {
    position: absolute;
    background-color: ${({ theme }) => theme.colors.background.tertiary};
    border-radius: 100%;
    height: 100%;
    width: auto;
  }

  ${handleCircleConfig}
`;

const fitImage = () => `
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

export const CoverImage = styled.img`
  ${fitImage}
  object-fit: cover;
  font-family: 'object-fit: cover';
  @media print {
    page-break-inside: avoid;
  }
`;

export const ContainedImage = styled.img`
  width: 100%;
  height: auto;

  .portrait & {
    width: auto;
    height: 176px;

    ${mq('sm')} {
      height: 347px;
    }
    ${mq('md')} {
      height: 472px;
    }
    ${mq('lg')} {
      height: 368px;
    }
  }
`;

export const Category = styled.p`
  ${text_xscap}
  margin: 0;
`;

export const Description = styled(StyledContentRichText)`
  ${text_l}

  margin: 0;
  
  ${mq.lessThan('md')}{
    line-height: ${(props) => props.$isBanner ? '28px' : '24px'};
  }
  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 3 })}
  }
`;

export const AttributesWrapper = styled.span`
  ${text_s}
  margin: 0;
  display: block;
  color: ${({ theme }) => theme.card.secondary};

  ${Container}.bg-tertiary & {
    color: ${({ theme }) => theme.colors.element.white};
    path {
      fill: ${({ theme }) => theme.colors.element.white};
    }
  }

  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 3 })}
  }
`;

export const SecondaryContainer = styled(Grid.Container)`
  position: relative;
  padding-bottom: 32px;

  margin-top: -${secondaryContainerMarginOffset('xs')}px;

  ${mq('md')} {
    margin-top: -${secondaryContainerMarginOffset('md')}px;
  }

  ${mq('lg')} {
    margin-top: -${secondaryContainerMarginOffset('lg')}px;
  }

  @media print {
    margin-top: 32px;
  }
`;

export const SecondaryRow = styled(Grid.Row)`
  list-style: none;
  padding: 0;
`;

export const SecondaryItem = styled(Grid.Item)`
  /*tighter vertical spacing then normal grid itesm */

  ${mq.lessThan('lg')} {
    &:not(:first-child) {
      ${({ theme }) => spacing({ theme, $mt: -1 })}
    }
  }

  ${mq('lg')} {
    margin-top: 0;
    align-self: stretch;
  }
`;

export const LinkIconContainer = styled(NavLink)`
  display: block;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.card.action};
  }

  ${Container}.bg-tertiary &:hover {
    color: ${({ theme }) => theme.colors.element.white};
  }
`;

export const LinkIconBannerContainer = styled(NavLink)`
  display: block;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.card.action};
  }
`;

// https://snook.ca/archives/html_and_css/icon-wrap-after-text
export const TitleIconWrapper = styled.span`
  white-space: nowrap;
  line-height: 0;
`;

export const ArrowWrapper = styled.span`
  ${svg(14, 14)}
  width: 19px;
  margin-left: 12px;
  display: inline-block;

  ${mq('lg')} {
    margin-left: 16px;
    width: 24px;
  }

  path {
    fill: ${({ theme }) => theme.card.action};
  }

  ${Container}.bg-tertiary & {
    path {
      fill: ${({ theme }) => theme.colors.element.white};
    }
  }
`;

export const ArrowBannerWrapper = styled.span`
  ${svg(14, 14)}
  width: 19px;
  margin-left: 12px;
  display: inline-block;

  ${mq('lg')} {
    margin-left: 16px;
    width: 24px;
  }

  path {
    fill: ${({ theme }) => theme.card.action};
  }
`;

export const Title = styled.h2`
  display: inline;
  h2& {
    ${heading_xl}
  }

  h3& {
    ${heading_l}
  }

  h4& {
    ${heading_m}
  }

  h5& {
    ${heading_s}
  }

  h6& {
    ${heading_xs}
  }

  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 1 })}
  }
`;

export const TitleBanner = styled.h1`
    ${heading_xxl}
`

export const GridContainer = styled(Grid.Container)`
  @media print {
    width: 50%;
    margin: 0;
    page-break-inside: avoid;
  }
`;

export const HeaderText = styled.p`
    ${heading_l}
`

export const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    ${({theme}) => spacing({theme, $mb: 3})};
    & svg {
        width: 32px;
        margin-right: 10px;
    }
    ${mq.lessThan('md')} {
        & svg {
            width: 24px;
        }
    }
`
